/**
 * What the Overview's analytics say (A8, audits/2026-10-21/overview-analytics-spec.md ред.
 * 3), where a runner can reach them. `pages/Overview.tsx` renders these and decides
 * nothing; the decisions are in `overview-stats.ts`.
 *
 * 🔴 **Every number names what it is not**, because each of these figures is narrower
 * than it looks: one placement (ALL_ON_TWITTER — X reads one per request), a sum over
 * campaigns (X's account-level figures carry no billing and no URL clicks), billed as X's
 * estimate for its last three days, and read from the mirror an hourly cycle writes — so
 * as old as `readAt`, and partial when the cycle reached fewer campaigns than X listed.
 *
 * 🔴 **«not read yet» is never «0».** A figure the route answers as null is nothing read;
 * a 0 is X's. A ratio over 0 or over nothing is «—», with its formula beside it.
 *
 * The word for money spent is `SPEND` from stats-copy.ts, the module allowed it; the
 * click metric is named as X names it — «URL clicks (url_clicks)» — until a live reading
 * has matched it to Ads Manager's «Link clicks» (spec, §4).
 */
import { microMoney } from "./money.ts";
import type { BarMetric, CampaignName } from "./overview-stats.ts";
import { SPEND, readAtText } from "./stats-copy.ts";

/** The section's heading, per account. */
export function sectionTitle(accountName: string): string {
	return `Analytics — ${accountName}`;
}

/** The four figures, named as the screen shows them. */
export const FIGURE_NAMES: Record<BarMetric, string> = {
	billedMicro: SPEND,
	impressions: "Impressions",
	engagements: "Engagements",
	urlClicks: "URL clicks (url_clicks)",
};

/** The two periods, side by side. */
export const PERIOD_FULL = "Last 7 full days";
export const PERIOD_WITH_TODAY = "With today so far";

/** Under the today column: what today is. */
export const TODAY_NOTE =
	"Today so far is the current day of the account's time zone, to the last whole hour, summed from X's hourly figures.";

/** «not read yet» — nothing was read for this figure. Never «0». */
export const NOT_READ_YET = "not read yet";

/** A figure: money through money.ts, a count as X sent it, nothing read as `NOT_READ_YET`. */
export function figureText(
	metric: BarMetric,
	value: number | null,
	currency: string | null,
): string {
	if (value === null) return NOT_READ_YET;
	return metric === "billedMicro" ? microMoney(value, currency) : String(value);
}

/** The three ratios, each with its formula for the hint beside it. */
export const RATIOS = {
	ctr: { name: "CTR", formula: "url_clicks ÷ impressions" },
	cpm: { name: "CPM", formula: "billed ÷ impressions × 1000" },
	cpc: { name: "CPC", formula: "billed ÷ url_clicks" },
} as const;

/** A ratio with no denominator, or over a figure not read. */
export const NO_RATIO = "—";

/**
 * A fraction as a percentage to two decimals, truncated — the same rule money.ts keeps for
 * amounts: never claim a hundredth the figure has not reached.
 */
export function percentText(fraction: number | null): string {
	if (fraction === null) return NO_RATIO;
	const basisPoints = Math.trunc(fraction * 10000);
	const whole = Math.trunc(basisPoints / 100);
	const rest = String(basisPoints - whole * 100).padStart(2, "0");
	return `${String(whole)}.${rest}%`;
}

/** CPM or CPC: micro-units through money.ts, or «—». */
export function costText(micro: number | null, currency: string | null): string {
	return micro === null ? NO_RATIO : microMoney(micro, currency);
}

/** The ratio's hint: its formula, and why it may be «—». */
export function ratioHint(formula: string): string {
	return `${formula} — «—» when the divisor is 0 or not read.`;
}

/** 🔴 Beside the sums: one placement. */
export const PLACEMENT_NOTE =
	"ALL_ON_TWITTER only — X reports one placement per request; SPOTLIGHT and TREND are not read.";

/** 🔴 Beside the sums: what they are a sum of. */
export const SUM_NOTE =
	"A sum over the account's campaigns: X's account-level figures carry no billing and no URL clicks, so this is never one account request.";

/** 🔴 Beside the billed figure: X's own caveat. */
export const ESTIMATE_NOTE =
	"Billed for the last 3 days is X's estimate, and the hourly cycle rewrites it until X settles it.";

/** 🔴 When the cycle reached fewer campaigns than X listed. */
export function partialText(read: number, listed: number): string {
	return `partial (${String(read)} of ${String(listed)} campaigns read)`;
}

/** A day whose sum covers fewer campaigns than X listed. */
export function dayPartialText(read: number, listed: number): string {
	return `${String(read)} of ${String(listed)} campaigns`;
}

/** When the figures were read, and whether that is old. */
export function readText(readAt: string | null, stale: boolean): string {
	if (readAt === null) return "No cycle has read the figures yet.";
	const at = readAtText(readAt);
	return stale
		? `${at} — stale: older than two hourly cycles, so the figures may be behind X.`
		: `${at} by the hourly cycle.`;
}

/**
 * 🔴 The last cycle did not finish, in its own word — and what that leaves on screen. The
 * cycle commits every chunk it read before it stops (lib/stats-mirror.ts, write → stop →
 * COMMIT), so the figures are a mix: newer for the campaigns it reached, older for the rest.
 * Until 2026-10-22 this said «see the cycle result» — a result this screen does not show —
 * and «the figures are from the last cycle that did», which the commit-per-chunk refutes
 * (the reviewer's HOLD on #178).
 */
export function cycleUnfinishedText(word: string): string {
	return `The last cycle did not finish (${word}): the campaigns it reached before stopping carry its newer figures, the rest are from earlier cycles.`;
}

/** Before the first cycle has written anything for this account. */
export const NOT_READ_YET_BANNER = {
	title: "No figures read yet for this account.",
	body: "The hourly cycle writes them into the console's mirror; until it has, every figure here says «not read yet» rather than 0.",
};

/** 🔴 While migration 0010 is not applied: no figures, and why. */
export function unavailableText(why: string): { title: string; body: string } {
	return {
		title: "The figures are not available: the mirror's table is missing.",
		body: `The console answered «${why}» — the migration has not been applied to this console's database, which is the owner's step. Nothing here is a zero.`,
	};
}

/** The read itself failed. */
export const READ_FAILED = "The figures could not be read from the console.";

/** A campaign row X answered with nothing served. */
export const NO_SERVING = "X: no serving";

/** The campaigns table. */
export const CAMPAIGNS_HEADING = "Campaigns over the last 7 full days";
export const CAMPAIGN_COLUMN = "Campaign";
export const DAYS_COLUMN = "Days read";
export const OPEN_CARD = "Open card";
export const NO_CAMPAIGNS = "No campaign has a figure in these days.";
/**
 * Under the campaigns table when it is too narrow for Engagements and URL clicks. Where
 * they are read: above, the account's totals — the same answer, summed, not one campaign's —
 * and each campaign's own on its card, over seven days (campaign-card-copy.ts, `METRICS`).
 */
export const NARROW_COLUMNS =
	"Engagements and URL clicks by campaign are not in this table at this width: this account's totals for these days are above, and each campaign's own figures are on its card (Open card), over seven days.";

/**
 * A full day where some campaigns' figures are still X's hourly sum, not its DAY figure
 * (`partialCampaigns`, contract «Overview stats») — counted in the day, and named.
 */
export function dayNotFinalText(notFinal: number, campaigns: number): string {
	return `${String(notFinal)} of ${String(campaigns)} campaigns not final for this day`;
}

/** The bars. */
export const BARS_HEADING = "By day";
export const BAR_METRIC_LABEL = "Show";
export function barTitle(day: string, text: string): string {
	return `${day}: ${text}`;
}

/** The signals beside the figures. */
export const SIGNALS_HEADING = "X's signals";

/**
 * Where «Open card» goes: the Campaigns screen with this account chosen and this
 * campaign's card open (`pages/Campaigns.tsx` reads both).
 */
export function cardHref(accountId: string, campaignId: string): string {
	return `/campaigns?account=${encodeURIComponent(accountId)}&card=${encodeURIComponent(campaignId)}`;
}

/**
 * On Campaigns, when the asked card's campaign is not among the rows read. `more` is the
 * screen's `cursor !== null` — X answered a next page beside the last one read
 * (campaign-copy.ts, `MORE_BANNER`) — so Load more is named only while there is one, and
 * never by where it sits.
 */
export function cardNotOnPage(campaignId: string, more: boolean): string {
	return more
		? `Campaign ${campaignId} is not among the campaigns read so far. Load more reads the next ones; press Card on its row once it is listed.`
		: `Campaign ${campaignId} is not among the campaigns X lists under this account: X has no more to read, and it is not among the ones read.`;
}

/**
 * Beside a campaign's id in the table: its name, or «name not read» with the reason. The
 * id is always shown — it is what the figures are keyed by.
 */
export function campaignNameText(n: CampaignName): string {
	switch (n.kind) {
		case "name":
			return n.name;
		case "reading":
			return "reading the name…";
		case "failed":
			return "name not read: the campaign list did not answer";
		case "not-on-page":
			return n.truncated
				? "name not read: past the one page of X's campaign list read here"
				: "name not read: X's campaign list no longer has it";
	}
}
